<script setup lang="ts">
import { useEventListener, useVModel } from '@vueuse/core'

/**
 * Полоса перемотки. Используется и для таймлайна, и для громкости.
 *
 * Буферизованные участки приходят массивом пар [начало, конец] — так их
 * можно нарисовать несколькими сегментами, а не одной полосой до конца
 * последнего буфера.
 *
 * Указатель обрабатываем через Pointer Events, а не mousedown/mousemove:
 * это разом покрывает мышь, палец и стилус. На mouse-событиях перемотка
 * пальцем не работала вообще — на телефоне полосу нельзя было двигать.
 */
const props = defineProps({
  min: { type: Number, default: 0 },
  max: { type: Number, default: 100 },
  bufferedRanges: {
    type: Array as PropType<[number, number][]>,
    default: () => [],
  },
  /**
   * Отмеченные отрезки — опенинг и эндинг. Рисуются поверх заполнения
   * светлой накладкой: видно, где заставка, ещё до того как до неё дошли,
   * и понятно, куда именно прыгает кнопка пропуска.
   */
  segments: {
    type: Array as PropType<{ start: number; end: number; label: string }[]>,
    default: () => [],
  },
  modelValue: { type: Number, required: true },
  /**
   * Отдавать новое значение только когда указатель отпустили.
   *
   * Нужно таймлайну. Каждая запись в currentTime — это настоящая перемотка:
   * MediaSource сбрасывает буферы и заново тянет сегменты, а при десятках
   * записей в секунду он за курсором не поспевает — картинка замирает, звук
   * доигрывает со старого места и отстаёт на несколько секунд. Пока тянут
   * ползунок, двигаем только его самого, а видео перематываем один раз.
   *
   * Громкости это не нужно: там запись бесплатная, и без неё не слышно,
   * что именно ты крутишь.
   */
  commitOnRelease: { type: Boolean, default: false },
})

/**
 * hover — секунда под указателем, ещё не выбранная.
 *
 * Нужна плееру, чтобы подготовить кадр превью. Отдельное событие, а не слот:
 * слот только рисует, а тут нужен побочный эффект — перемотка скрытого видео.
 */
const emit = defineEmits(['update:modelValue', 'hover'])

const scrubber = ref<HTMLElement | null>(null)
const track = ref<HTMLElement | null>(null)
const scrubbing = ref(false)
const pendingValue = ref(0)
// Положение указателя внутри полосы — для подсказки со временем
const pointerX = ref(0)
const trackWidth = ref(0)

const value = useVModel(props, 'modelValue', emit)

function applyPointer(event: PointerEvent, commit: boolean) {
  const el = track.value
  if (!el) return

  const rect = el.getBoundingClientRect()
  if (!rect.width) return

  pointerX.value = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
  trackWidth.value = rect.width

  pendingValue.value = (pointerX.value / rect.width) * props.max
  if (commit) value.value = pendingValue.value

  // Только мышь: у тача наведения нет — палец либо тянет ползунок, либо не
  // касается полосы вовсе, и готовить превью «на будущее» там незачем
  if (event.pointerType === 'mouse') emit('hover', pendingValue.value)
}

/**
 * Захват указателя: палец уезжает за пределы восьмипиксельной полосы почти
 * сразу, а перемотка должна продолжаться, пока его не отпустили.
 *
 * В try/catch, потому что setPointerCapture бросает NotFoundError, если
 * указателя с таким id уже нет. Перемотка при этом работает и без захвата
 * (есть слушатели на window), так что падать тут не из-за чего.
 */
function capturePointer(event: PointerEvent, capture: boolean) {
  try {
    if (capture) scrubber.value?.setPointerCapture(event.pointerId)
    else scrubber.value?.releasePointerCapture(event.pointerId)
  } catch {
    /* указатель уже отпущен */
  }
}

function onPointerDown(event: PointerEvent) {
  capturePointer(event, true)
  scrubbing.value = true
  // Клик по полосе — сразу перемотка в эту точку, без предварительного драга
  applyPointer(event, true)
}

function onPointerMove(event: PointerEvent) {
  applyPointer(event, scrubbing.value && !props.commitOnRelease)
}

function stopScrubbing(event?: PointerEvent) {
  if (!scrubbing.value) return
  if (event) capturePointer(event, false)
  scrubbing.value = false
  // Та самая единственная перемотка за весь драг
  if (props.commitOnRelease) value.value = pendingValue.value
}

/**
 * Положение ползунка. Пока его тянут, это позиция пальца, а не времени в
 * видео: с commitOnRelease видео туда ещё не уехало, и ползунок иначе стоял
 * бы на месте.
 */
const displayValue = computed(() =>
  scrubbing.value && props.commitOnRelease ? pendingValue.value : props.modelValue
)

// Отпустить можно и вне полосы, и вне окна — например, уведя палец за край
useEventListener('pointerup', () => stopScrubbing())
useEventListener('pointercancel', () => stopScrubbing())

const getBufferedBarStyles = (start: number, end: number) => ({
  left: `${(start / props.max) * 100}%`,
  width: `${((end - start) / props.max) * 100}%`,
})

/**
 * У отмеченного отрезка конец бывает и за пределами длительности: тайминги
 * Kodik заведены по своей копии серии, а она на пару секунд длиннее или
 * короче нашей. Обрезаем по краям полосы, иначе накладка вылезает за неё.
 */
const getSegmentStyles = (start: number, end: number) => {
  const left = Math.max(0, Math.min(100, (start / props.max) * 100))
  const right = Math.max(0, Math.min(100, (end / props.max) * 100))
  return { left: `${left}%`, width: `${Math.max(0, right - left)}%` }
}
</script>

<template>
  <!--
    py-2/-my-2 расширяют зону нажатия до 24px по вертикали, не меняя вёрстку:
    попасть пальцем в полосу высотой 8px невозможно.
    touch-none обязателен — иначе браузер считает движение вдоль полосы
    прокруткой страницы и перемотка срывается на первом же пикселе.
  -->
  <div
    ref="scrubber"
    class="group/scrubber relative -my-2 py-2 touch-none cursor-pointer select-none"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="stopScrubbing"
    @pointercancel="stopScrubbing"
  >
    <!-- w-full обязателен: ползунок громкости получает снаружи класс `flex`,
         и без явной ширины дорожка схлопывается в ноль как flex-элемент -->
    <div ref="track" class="relative bg-white bg-opacity-10 rounded w-full h-2">
      <div class="relative rounded w-full h-full overflow-hidden">
        <div
          v-for="(range, index) in bufferedRanges"
          :key="index"
          class="absolute bg-primary opacity-30 rounded h-full"
          :style="getBufferedBarStyles(range[0], range[1])"
        />
        <!-- Заполнение сдвигом, а не шириной: transform не вызывает layout
             и не дёргается при частых обновлениях времени -->
        <div
          class="relative bg-primary rounded w-full h-full"
          :style="{ transform: `translateX(${(displayValue / max) * 100 - 100}%)` }"
        />

        <!-- Поверх заполнения, а не под ним: иначе просмотренный опенинг
             сливался бы с обычной полосой и метка пропадала -->
        <div
          v-for="segment in segments"
          :key="`${segment.start}-${segment.label}`"
          class="absolute inset-y-0 bg-white/45"
          :style="getSegmentStyles(segment.start, segment.end)"
          :title="segment.label"
        />
      </div>

      <!-- Бегунок виден всегда: на телефоне нет наведения, и без него
           непонятно, за что тянуть -->
      <div
        class="top-1/2 absolute bg-primary rounded-full w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        :style="{ left: `${Math.max(0, Math.min(100, (displayValue / max) * 100))}%` }"
      />

      <div
        class="absolute inset-0 opacity-0 group-hover/scrubber:opacity-100"
        :class="{ '!opacity-100': scrubbing }"
      >
        <slot
          :pending-value="pendingValue"
          :position="`${Math.max(0, Math.min(pointerX, trackWidth))}px`"
        />
      </div>
    </div>
  </div>
</template>
